import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@components/icons';
import {
  CaseStudy,
  Reveal,
  ProjectHeader,
  TechChips,
  SectionHeading,
  Note,
  TechList,
  Gallery,
} from '@components/casestudy';
import shot1 from '@images/joe/1.png';
import shot2 from '@images/joe/2.png';

const StyledStoreLink = styled.a`
  ${({ theme }) => theme.mixins.smallButton};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 26px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const JoePage = ({ location }) => (
  <CaseStudy location={location} title="Joe">
    <Reveal>
      <ProjectHeader
        eyebrow="Freelance · Développeur mobile"
        title="Joe"
        tagline="Application VTC en production"
        meta={['2024 → présent', 'Publiée sur l’App Store et Google Play']}>
        <TechChips
          items={[
            { label: 'Node.js', hot: true },
            { label: 'Temps réel', hot: true },
            { label: 'API REST', hot: true },
            { label: 'Flutter / Dart' },
            { label: 'Firebase' },
            { label: 'Kotlin' },
            { label: 'Swift' },
            { label: 'Clean Architecture' },
          ]}
        />
      </ProjectHeader>
    </Reveal>

    <Reveal>
      <SectionHeading>Le projet</SectionHeading>
      <p className="cs-lead">
        Application des chauffeurs VTC et taxi d’une flotte lyonnaise : réception des courses en
        temps réel, acceptation, navigation, puis suivi des revenus et des factures. Backend{' '}
        <strong>Node.js</strong> et Firebase, avec une API REST. J’ai repris une base héritée, l’ai
        restructurée en <strong>Clean Architecture</strong>, corrigé les bugs bloquants et publié sur
        les deux stores.
      </p>
      <p className="cs-lead">
        Le framework ne couvre pas tout : j’ai écrit du <strong>code natif Swift et Kotlin</strong>{' '}
        pour les permissions, les services en arrière-plan et les intégrations système que réclame
        une application de terrain utilisée toute la journée.
      </p>
    </Reveal>

    <Reveal>
      <SectionHeading>Le produit</SectionHeading>
      <Gallery
        columns={1}
        columnsSm={1}
        items={[
          {
            src: shot1,
            alt: 'Joe, réception d’une course en temps réel',
            caption: (
              <>
                <b>Réception de course en temps réel.</b> Le chauffeur passe en ligne, reçoit une
                proposition (prix, distance, départ, arrivée) et l’accepte. Notifications push et
                géolocalisation.
              </>
            ),
          },
          {
            src: shot2,
            alt: 'Joe, revenus et facturation',
            caption: (
              <>
                <b>Revenus &amp; facturation.</b> Historique des courses, revenu net, frais de service
                et factures par période, servis par l’API.
              </>
            ),
          },
        ]}
      />
      <StyledStoreLink
        href="https://apps.apple.com/us/app/joe-commandez-votre-course/id6502338791"
        target="_blank"
        rel="noreferrer">
        <Icon name="AppStore" /> Voir sur l’App Store
      </StyledStoreLink>
    </Reveal>

    <Reveal>
      <SectionHeading>Ce que j’en retiens</SectionHeading>
      <p className="cs-lead">
        Je le mets ici pour une raison précise : c’est le projet où j’ai appris ce qui se passe{' '}
        <strong>après</strong> le développement. Un bug en production a un visage, un chauffeur qui ne
        reçoit pas sa course. C’est ce qui m’a rendu méthodique sur la qualité et les relectures de
        code.
      </p>
      <Note>
        <b>Livrer, corriger, maintenir.</b> Pas seulement développer. L’application est utilisée au
        quotidien, et elle reste maintenue en production avec de vrais utilisateurs.
      </Note>
    </Reveal>

    <Reveal>
      <SectionHeading>Technologies</SectionHeading>
      <TechList
        items={[
          'Flutter',
          'Dart',
          'Node.js',
          'Firebase',
          'API REST',
          'Swift',
          'Kotlin',
          'Clean Architecture',
        ]}
      />
    </Reveal>
  </CaseStudy>
);

JoePage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default JoePage;
